import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {Button} from '@ui/buttons/button';
import {Skeleton} from '@ui/skeleton/skeleton';
import {ApiIcon} from '@common/icons/material/Api';
import {UploadFileIcon} from '@common/icons/material/UploadFile';
import {AnalyticsIcon} from '@common/icons/material/Analytics';
import {LibraryMusicIcon} from '@common/icons/material/LibraryMusic';
import {AssessmentIcon} from '@common/icons/material/Assessment';
import {PeopleIcon} from '@common/icons/material/People';
import {KeyIcon} from '@common/icons/material/Key';
import {WebhookIcon} from '@common/icons/material/Webhook';

interface GadPartnerAccount {
  id: number;
  company_name: string;
  trade_name?: string;
  company_type: string;
  status: string;
  corporate_email: string;
}

interface MeResponse {
  partner_account: GadPartnerAccount | null;
}

interface GadAnalytics {
  tracks: {
    total_streams: number;
    total_likes: number;
    total_shares: number;
    playlist_adds: number;
    saves: number;
    retention_seconds: number;
    daily_growth: Array<{date: string; value: number}>;
    top_tracks: Array<{id: number; name: string; artist?: string; plays: number}>;
  };
  albums: {
    total_plays: number;
    likes: number;
    shares: number;
    saves: number;
    top_albums: Array<{id: number; name: string; artist?: string; plays: number}>;
  };
  geography: Array<{label: string; value: number}>;
  technical: {
    device: Array<{label: string; value: number}>;
    platform: Array<{label: string; value: number}>;
    browser: Array<{label: string; value: number}>;
  };
}

const validationMessage =
  'Sua conta está em processo de validação e análise documental. Assim que a aprovação for concluída, o acesso completo ao painel e às APIs será liberado.';

const menus = [
  {label: 'APIs', icon: ApiIcon},
  {label: 'Uploads', icon: UploadFileIcon},
  {label: 'Analytics', icon: AnalyticsIcon},
  {label: 'Catálogo', icon: LibraryMusicIcon},
  {label: 'Financeiro', icon: AssessmentIcon},
  {label: 'Relatórios', icon: AssessmentIcon},
  {label: 'Logs', icon: KeyIcon},
  {label: 'Webhooks', icon: WebhookIcon},
  {label: 'Equipes', icon: PeopleIcon},
];

export function DashboardPartnerGadPage() {
  const meQuery = useQuery({
    queryKey: ['gad', 'me'],
    queryFn: () => apiClient.get<MeResponse>('gad/me').then(r => r.data),
  });

  const account = meQuery.data?.partner_account;
  const approved = account?.status === 'approved';

  const analyticsQuery = useQuery({
    queryKey: ['gad', 'analytics'],
    enabled: approved,
    queryFn: () =>
      apiClient
        .get<{analytics: GadAnalytics}>('gad/analytics')
        .then(r => r.data.analytics),
  });

  const keysQuery = useQuery({
    queryKey: ['gad', 'api-keys'],
    enabled: approved,
    queryFn: () =>
      apiClient
        .get<{api_keys: Array<{id: number; name: string; environment: string; key_prefix: string; status: string}>}>('gad/api-keys')
        .then(r => r.data.api_keys),
  });

  if (meQuery.isLoading) {
    return (
      <main className="min-h-screen bg-alt p-24">
        <div className="mx-auto max-w-1280">
          <Skeleton className="mb-24 h-36 w-240" />
          <div className="grid gap-16 md:grid-cols-3">
            {Array.from({length: 6}).map((_, index) => (
              <Skeleton key={index} className="h-96" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (!account) {
    return (
      <main className="min-h-screen bg-alt">
        <div className="mx-auto flex max-w-720 flex-col items-start px-24 py-64">
          <h1 className="mb-12 text-3xl font-semibold">Dashboard GAD</h1>
          <p className="mb-24 text-muted">
            Nenhuma conta parceira GAD foi encontrada para este usuário.
          </p>
          <Button elementType={Link} to="/register-partner-account-gad" variant="flat" color="primary">
            Criar conta parceira
          </Button>
        </div>
      </main>
    );
  }

  if (!approved) {
    return (
      <main className="min-h-screen bg-alt">
        <div className="mx-auto max-w-920 px-24 py-64">
          <div className="rounded border bg-paper p-24">
            <div className="mb-8 text-sm font-semibold uppercase text-primary">
              {statusLabel(account.status)}
            </div>
            <h1 className="mb-16 text-3xl font-semibold">{account.company_name}</h1>
            <p className="text-base text-muted">{validationMessage}</p>
          </div>
        </div>
      </main>
    );
  }

  const analytics = analyticsQuery.data;

  return (
    <main className="min-h-screen bg-alt">
      <div className="mx-auto max-w-1280 px-24 py-32">
        <div className="mb-24 flex flex-wrap items-center justify-between gap-16">
          <div>
            <div className="mb-6 text-sm font-semibold uppercase text-positive">Conta aprovada</div>
            <h1 className="text-3xl font-semibold">{account.trade_name || account.company_name}</h1>
            <p className="mt-6 text-sm text-muted">
              Catálogo, chaves, logs, webhooks e analytics escopados por parceiro.
            </p>
          </div>
          <Button elementType={Link} to="/register-partner-account-gad" variant="outline">
            Atualizar cadastro
          </Button>
        </div>

        <div className="mb-28 grid gap-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {menus.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                className="flex h-72 items-center gap-12 rounded border bg-paper px-16 text-left text-sm font-semibold transition hover:border-primary"
              >
                <Icon size="md" className="text-primary" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mb-28 grid gap-16 md:grid-cols-2 lg:grid-cols-4">
          <Metric label="Total Streams" value={analytics?.tracks.total_streams} />
          <Metric label="Likes" value={analytics?.tracks.total_likes} />
          <Metric label="Shares" value={analytics?.tracks.total_shares} />
          <Metric label="Playlist Adds" value={analytics?.tracks.playlist_adds} />
          <Metric label="Saves" value={analytics?.tracks.saves} />
          <Metric label="Retenção" value={analytics?.tracks.retention_seconds} suffix="s" />
          <Metric label="Plays de álbuns" value={analytics?.albums.total_plays} />
          <Metric label="Álbuns salvos" value={analytics?.albums.saves} />
        </div>

        <div className="grid gap-20 lg:grid-cols-2">
          <Panel title="Top Tracks">
            <RankedList items={analytics?.tracks.top_tracks || []} />
          </Panel>
          <Panel title="Top Albums">
            <RankedList items={analytics?.albums.top_albums || []} />
          </Panel>
          <Panel title="Analytics geográficos">
            <Breakdown items={analytics?.geography || []} empty="Sem dados geográficos ainda." />
          </Panel>
          <Panel title="Analytics técnicos">
            <div className="grid gap-16 md:grid-cols-3">
              <Breakdown title="Dispositivo" items={analytics?.technical.device || []} />
              <Breakdown title="Sistema" items={analytics?.technical.platform || []} />
              <Breakdown title="Plataforma" items={analytics?.technical.browser || []} />
            </div>
          </Panel>
          <Panel title="API Keys">
            <div className="space-y-10">
              {(keysQuery.data || []).map(key => (
                <div key={key.id} className="flex flex-wrap items-center justify-between gap-12 border-b pb-10 last:border-b-0 last:pb-0">
                  <div>
                    <div className="font-semibold">{key.name}</div>
                    <div className="text-xs text-muted">
                      {key.environment} · {key.key_prefix} · {key.status}
                    </div>
                  </div>
                </div>
              ))}
              {!keysQuery.data?.length && <div className="text-sm text-muted">Nenhuma chave gerada ainda.</div>}
            </div>
          </Panel>
          <Panel title="Crescimento diário">
            <Breakdown
              items={(analytics?.tracks.daily_growth || []).map(item => ({
                label: item.date,
                value: item.value,
              }))}
              empty="Sem crescimento diário registrado ainda."
            />
          </Panel>
        </div>
      </div>
    </main>
  );
}

function Metric({label, value, suffix}: {label: string; value?: number; suffix?: string}) {
  return (
    <div className="rounded border bg-paper p-18">
      <div className="mb-8 text-xs font-semibold uppercase text-muted">{label}</div>
      <div className="text-3xl font-semibold">{value ?? 0}{suffix || ''}</div>
    </div>
  );
}

function Panel({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <section className="rounded border bg-paper p-20">
      <h2 className="mb-16 text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function RankedList({items}: {items: Array<{id: number; name: string; artist?: string; plays: number}>}) {
  if (!items.length) {
    return <div className="text-sm text-muted">Sem dados suficientes ainda.</div>;
  }

  return (
    <div className="space-y-12">
      {items.map((item, index) => (
        <div key={`${item.id}-${index}`} className="flex items-center justify-between gap-16">
          <div className="min-w-0">
            <div className="truncate font-semibold">{index + 1}. {item.name}</div>
            {item.artist && <div className="truncate text-xs text-muted">{item.artist}</div>}
          </div>
          <div className="text-sm font-semibold">{item.plays}</div>
        </div>
      ))}
    </div>
  );
}

function Breakdown({
  title,
  items,
  empty = 'Sem dados ainda.',
}: {
  title?: string;
  items: Array<{label: string; value: number}>;
  empty?: string;
}) {
  return (
    <div>
      {title && <div className="mb-8 text-sm font-semibold">{title}</div>}
      {items.length ? (
        <div className="space-y-8">
          {items.slice(0, 8).map(item => (
            <div key={item.label || 'unknown'} className="flex items-center justify-between gap-12 text-sm">
              <span className="truncate text-muted">{item.label || 'N/A'}</span>
              <span className="font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted">{empty}</div>
      )}
    </div>
  );
}

function statusLabel(status: string): string {
  return {
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    suspended: 'Suspended',
    validation_required: 'Validation Required',
    blocked: 'Blocked',
  }[status] || status;
}
